import type { Item, ModifierGroup } from "@luckymenu/core";
import type { DraftLine } from "../ordering/draft";
import { groupsOf } from "./view";

/**
 * What staff pick for one item in the item picker: a size and add-ons. Pure,
 * so the web dialog (right click) and the Android sheet (long press) behave
 * the same. Prices are integer paise; the server prices every line again.
 */
export type Choice = {
	variantId: string | null;
	/** Option IDs per add-on group ID. */
	options: Record<string, string[]>;
};

/** Staff may order items hidden from the menu, but nothing sold out (api.md, createOrder). */
export function canOrder(item: Item): boolean {
	return (
		item.isAvailable && (item.variants.length === 0 || item.variants.some((v) => v.isAvailable))
	);
}

/** The default size if it is available, else the first available one. No add-ons. */
export function initialChoice(item: Item): Choice {
	const available = item.variants.filter((v) => v.isAvailable);
	const variant = available.find((v) => v.isDefault) ?? available[0] ?? null;
	return { variantId: variant?.id ?? null, options: {} };
}

/**
 * A tap adds one at once when nothing must be chosen: no required add-on
 * group (sizes take the default). Otherwise the choice dialog opens.
 */
export function needsChoice(item: Item, groups: readonly ModifierGroup[]): boolean {
	return groupsOf(item, groups).some((g) => g.minSelect > 0);
}

/**
 * Ticks or unticks an option. A group of at most one works like radio
 * buttons; in others an option is refused once the maximum is reached.
 * Sold-out options cannot be ticked.
 */
export function toggleOption(choice: Choice, group: ModifierGroup, optionId: string): Choice {
	const option = group.options.find((o) => o.id === optionId);
	if (!option) return choice;
	const current = choice.options[group.id] ?? [];
	let next: string[];
	if (current.includes(optionId)) next = current.filter((id) => id !== optionId);
	else if (!option.isAvailable) return choice;
	else if (group.maxSelect === 1) next = [optionId];
	else if (current.length >= group.maxSelect) return choice;
	else next = [...current, optionId];
	return { ...choice, options: { ...choice.options, [group.id]: next } };
}

export function chooseVariant(item: Item, choice: Choice, variantId: string): Choice {
	const variant = item.variants.find((v) => v.id === variantId);
	return variant?.isAvailable ? { ...choice, variantId } : choice;
}

/** Groups still short of their minimum. Empty when the choice can be added. */
export function missingGroups(
	item: Item,
	groups: readonly ModifierGroup[],
	choice: Choice,
): ModifierGroup[] {
	return groupsOf(item, groups).filter((g) => (choice.options[g.id]?.length ?? 0) < g.minSelect);
}

export function isComplete(item: Item, groups: readonly ModifierGroup[], choice: Choice): boolean {
	if (item.variants.length > 0 && !choice.variantId) return false;
	return missingGroups(item, groups, choice).length === 0;
}

/**
 * The draft line for a choice: what is sent (item, size, options) and what
 * the screen shows until the server answers (names and prices as picked).
 */
export function lineFromChoice(
	item: Item,
	groups: readonly ModifierGroup[],
	choice: Choice,
	quantity = 1,
	note: string | null = null,
): DraftLine {
	const variant = item.variants.find((v) => v.id === choice.variantId) ?? null;
	const modifiers = groupsOf(item, groups).flatMap((g) =>
		g.options
			.filter((o) => (choice.options[g.id] ?? []).includes(o.id))
			.map((o) => ({ id: o.id, name: o.name, priceDeltaPaise: o.priceDeltaPaise })),
	);
	return {
		itemId: item.id,
		variantId: variant?.id ?? null,
		modifierOptionIds: modifiers.map((m) => m.id),
		quantity,
		note,
		name: item.name,
		variantName: variant?.name ?? null,
		diet: item.diet,
		modifiers,
		unitPricePaise: variant?.pricePaise ?? item.basePricePaise,
		modifiersTotalPaise: modifiers.reduce((n, m) => n + m.priceDeltaPaise, 0),
	};
}

/** One of `item` as a tap adds it, or null when a choice must be made first (or it is sold out). */
export function quickLine(item: Item, groups: readonly ModifierGroup[]): DraftLine | null {
	if (!canOrder(item) || needsChoice(item, groups)) return null;
	return lineFromChoice(item, groups, initialChoice(item));
}

/** The price of one of this item as chosen. */
export function choiceUnitPaise(
	item: Item,
	groups: readonly ModifierGroup[],
	choice: Choice,
): number {
	const line = lineFromChoice(item, groups, choice);
	return line.unitPricePaise + line.modifiersTotalPaise;
}
